import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import customBaseQuery from "./baseQuery";

// Types for API requests
export interface RegisterRequest {
  [key: string]: string | Blob; // For FormData (supports files too)
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface ForgetPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  email: string;
  newPassword: string;
  token: string;
}

// Response types (you can adjust these based on your actual API response)
export interface AuthResponse {
  token: string;
  user: {
    id: string;
    name: string;
    email: string;
    [key: string]: any;
  };
}

export interface GenericResponse {
  message: string;
}

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    register: builder.mutation<AuthResponse, RegisterRequest>({
      query: (formDataObj) => {
        const formData = new FormData();
        for (const key in formDataObj) {
          formData.append(key, formDataObj[key]);
        }

        return {
          url: "/auth/register",
          method: "POST",
          body: formData,
        };
      },
    }),
    login: builder.mutation<AuthResponse, LoginRequest>({
      query: (credentials) => ({
        url: "/auth/login",
        method: "POST",
        body: JSON.stringify(credentials),
        headers: {
          "Content-Type": "application/json",
        },
      }),
    }),
    forgetPassword: builder.mutation<GenericResponse, ForgetPasswordRequest>({
      query: (credentials) => ({
        url: "/auth/forget-password",
        method: "POST",
        body: JSON.stringify(credentials),
        headers: {
          "Content-Type": "application/json",
        },
      }),
    }),
    resetPassword: builder.mutation<GenericResponse, ResetPasswordRequest>({
      query: (credentials) => ({
        url: "/auth/reset-password",
        method: "POST",
        body: JSON.stringify(credentials),
        headers: {
          "Content-Type": "application/json",
        },
      }),
    }),
    refreshToken: builder.mutation<AuthResponse, LoginRequest>({
      query: (credentials) => ({
        url: "/auth/refresh-token",
        method: "POST",
        body: JSON.stringify(credentials),
        headers: {
          "Content-Type": "application/json",
        },
      }),
    }),
    validToken: builder.mutation<AuthResponse, LoginRequest>({
      query: (credentials) => ({
        url: "/auth/validate/token",
        method: "POST",
        body: JSON.stringify(credentials),
        headers: {
          "Content-Type": "application/json",
        },
      }),
    }),
  }),
});

export const {
  useRegisterMutation,
  useLoginMutation,
  useForgetPasswordMutation,
  useResetPasswordMutation,
  useRefreshTokenMutation,
  useValidTokenMutation,
} = authApi;
